import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { v4 as uuid } from 'uuid';
import dotProp from 'dot-prop-immutable';
import { motion, AnimatePresence } from 'framer-motion';
import Select from 'react-select';
import axiosUI from '../../axiosUI';
import Loader from '../Loader/Loader';
import Input, { INPUT_TYPES } from '../Input/Input';
import Button from '../Button/Button';
import Roles from './Roles';
import DeleteUser from './DeleteUser';
import withCommonUtils from '../withCommonUtils';
import { withGlobalContext } from '../../App';
import { NOTIFICATIONS } from '../../constants';

class Permissions extends Component {
  static propTypes = {
    getSingleSelectValue: PropTypes.func.isRequired,
    getMultiSelectValue: PropTypes.func.isRequired,
    createNotification: PropTypes.func.isRequired,
  };

  constructor(props) {
    super(props);

    this.state = {
      users: [],
      availalbePermissions: [],
      availableRoles: [],
      newRolePermissions: [],
      newRoleName: '',
      loading: false,
      showAddRolePopup: false,
      showDeleteWarningPopup: false,
      isAuthorized: false,
    };
  }

  componentDidMount = async () => {
    this.setState({ loading: true }, async () => {
      try {
        await Promise.all([
          this.fetchAllPermissionsAndSetState(),
          this.fetchAllUsersAndSetState(),
          this.fetchAllRolesAndSetState(),
        ]);

        this.setState({ isAuthorized: true });
      } catch (error) {
        console.log(error);
      }

      this.setState({ loading: false });
    });
  };

  fetchAllPermissionsAndSetState = async () => {
    try {
      const url = '/api/access/permissions';
      const response = await axiosUI.get(url);
      const availalbePermissions = response.data.map(p => {
        return {
          label: p.name,
          value: p.id,
        };
      });
      this.setState({ availalbePermissions });
    } catch (error) {
      console.log(error);
    }
  };

  fetchAllUsersAndSetState = async () => {
    try {
      const url = '/api/access/users';
      const response = await axiosUI.get(url);
      this.setState({
        users: response.data.map(user => {
          return {
            name: user.name,
            role: user.role.id,
          };
        }),
      });
    } catch (error) {
      console.log(error);
    }
  };

  fetchAllRolesAndSetState = async () => {
    try {
      const url = '/api/access/roles';
      const response = await axiosUI.get(url);
      this.setState({
        availableRoles: response.data.map(role => {
          return {
            ...role,
            label: role.name,
            value: role.id,
          };
        }),
      });
    } catch (error) {
      console.log(error);
    }
  };

  toggleAddNewRolePopup = () => {
    this.setState(prevState => ({
      showAddRolePopup: !prevState.showAddRolePopup,
    }));
  };

  onChange = (target, value) => {
    // this function handles input field update for all state params
    // target string will be of type: users.2.permissions
    this.setState(dotProp.set(this.state, target, value));
  };

  onChangeUserField = (fieldName, index, value) => {
    // when a user field is changed, we also need to track
    // which all users are unsaved, and show "Save Changes" button
    // for each of them

    const currentUser = this.state.users[index];

    const updatedUser = {
      ...currentUser,
      unsaved: true,
      [fieldName]: value,
    };

    const fieldPath = `users.${index}`;
    this.onChange(fieldPath, updatedUser);
  };

  addNewUser = () => {
    const newUserConfig = {
      name: '',
      role: '',
      uiGeneratedUUID: uuid(),
    };

    this.setState({ users: [newUserConfig, ...this.state.users] });
  };

  isDuplicateUserName = index => {
    const currentUserName = this.state.users[index].name;

    // we want to check if there is any other user if same name
    // at different index
    return this.state.users.some((user, userIndex) => {
      return user.name === currentUserName && userIndex !== index;
    });
  };

  submitRole = async () => {
    this.setState({ loading: true }, async () => {
      try {
        const url = '/api/access/roles';

        await axiosUI.post(url, {
          name: this.state.newRoleName,
          account_permissions: this.state.newRolePermissions.map(id => ({
            id,
          })),
        });

        await this.fetchAllRolesAndSetState();

        this.props.createNotification({
          message: `Role ${this.state.newRoleName} is created`,
          type: NOTIFICATIONS.TYPE.INFO,
          timeout: 5000,
        });

        // clear popup data
        this.setState({ newRoleName: '', newRolePermissions: [] });
      } catch (error) {
        console.log(error);

        this.props.createNotification({
          message: `Failed to create new role`,
          type: NOTIFICATIONS.TYPE.ERROR,
          timeout: 3000,
        });
      }

      this.setState({ loading: false });
    });
  };

  submitUser = async index => {
    this.setState({ loading: true }, async () => {
      const currentUser = this.state.users[index];
      const userPayload = {
        name: currentUser.name,
        type: 'USER',
        role: this.state.availableRoles.find(role => {
          return role.id === currentUser.role;
        }),
      };
      const url = `/api/access`;

      try {
        const { data } = await axiosUI.post(url, userPayload);
        const updatedUser = {
          name: data.name,
          role: data.role.id,
        };

        // update user in table after successful api call
        this.setState({
          users: dotProp.set(this.state.users, index, updatedUser),
        });

        this.props.createNotification({
          message: `User ${currentUser.name} is updated/created`,
          type: NOTIFICATIONS.TYPE.INFO,
          timeout: 5000,
        });
      } catch (error) {
        console.log(error);

        this.props.createNotification({
          message: `Failed to update/create ${currentUser.name}`,
          type: NOTIFICATIONS.TYPE.ERROR,
          timeout: 3000,
        });
      }

      this.setState({ loading: false });
    });
  };

  toggleDeleteWarningPopup = index => {
    this.setState(prevState => ({
      showDeleteWarningPopup:
        prevState.showDeleteWarningPopup === index ? false : index,
    }));
  };

  deleteUser = async index => {
    const currentUser = this.state.users[index];

    if (currentUser.uiGeneratedUUID) {
      const updatedUserList = [
        ...this.state.users.slice(0, index),
        ...this.state.users.slice(index + 1),
      ];

      return this.setState({ users: updatedUserList });
    }

    this.setState({ loading: true }, async () => {
      try {
        const url = `/api/access/${currentUser.name}`;
        await axiosUI.delete(url);

        const updatedUserList = [
          ...this.state.users.slice(0, index),
          ...this.state.users.slice(index + 1),
        ];

        this.setState({ users: updatedUserList });

        this.props.createNotification({
          message: `User "${currentUser.name}" is deleted`,
          type: NOTIFICATIONS.TYPE.INFO,
          timeout: 5000,
        });
      } catch (error) {
        console.log(error);

        this.props.createNotification({
          message: `Failed to delete user "${currentUser.name}"`,
          type: NOTIFICATIONS.TYPE.ERROR,
          timeout: 3000,
        });
      }

      this.setState({ loading: false });
    });
  };

  render() {
    return (
      <div className="bg-white m-auto max-w-md p-4 rounded shadow-md mb-10 relative">
        <Loader loading={this.state.loading}>
          <div className="flex justify-between items-center">
            <span className="font-black">User Access & Permissions</span>
            {this.state.isAuthorized && (
              <div className="flex">
                <Roles
                  {...this.state}
                  onChange={this.onChange}
                  submitRole={this.submitRole}
                  toggleAddNewRolePopup={this.toggleAddNewRolePopup}
                  getMultiSelectValue={this.props.getMultiSelectValue}
                />
                <Button
                  type="blue"
                  className="h-8 ml-8"
                  onClick={this.addNewUser}>
                  Add new User
                </Button>
              </div>
            )}
          </div>
          <ul className="list-reset m-0 mt-12">
            <AnimatePresence initial={false}>
              {this.state.users.map((user, index) => {
                const error =
                  user.uiGeneratedUUID && this.isDuplicateUserName(index)
                    ? 'username should be unqiue'
                    : '';
                const showSaveChangesButton = (
                  user.unsaved
                  && user.name
                  && user.role
                  && !error
                );
                return (
                  <motion.li
                    initial={{ opacity: 0.5, left: '-50px' }}
                    animate={{ opacity: 1, left: '0px' }}
                    exit={{ opacity: 0, left: '50px' }}
                    transition={{
                      ease: [0.18, 0.89, 0.39, 1.4],
                      duration: 0.3,
                    }}
                    positionTransition
                    className="flex justify-between items-center mt-4 relative"
                    key={user.uiGeneratedUUID || user.name}>
                    <div className="flex items-center">
                      <Input
                        disabled={!user.uiGeneratedUUID}
                        id={`users.${index}.name`}
                        name={`users.${index}.name`}
                        type={INPUT_TYPES.TEXT}
                        placeholder="Enter username"
                        value={user.name}
                        onChange={event =>
                          this.onChangeUserField(
                            'name',
                            index,
                            event.target.value
                          )
                        }
                        error={error}
                        className="w-48"
                        inputClassName="shadow-inner bg-almostWhite"
                      />
                      <Select
                        isSearchable
                        placeholder="Select role"
                        options={this.state.availableRoles}
                        classNamePrefix="formatted_react_select"
                        className="w-48 ml-8 embossed"
                        value={this.props.getSingleSelectValue(
                          this.state.availableRoles,
                          user.role
                        )}
                        onChange={valueObject =>
                          this.onChangeUserField(
                            'role',
                            index,
                            valueObject.value
                          )
                        }
                      />
                      {showSaveChangesButton && (
                        <Button
                          type="green"
                          className="h-8 ml-8"
                          onClick={() => this.submitUser(index)}>
                          Save Changes
                        </Button>
                      )}
                    </div>
                    <DeleteUser
                      index={index}
                      user={user}
                      showDeleteWarningPopup={this.state.showDeleteWarningPopup}
                      toggleDeleteWarningPopup={this.toggleDeleteWarningPopup}
                      deleteUser={this.deleteUser}
                    />
                  </motion.li>
                );
              })}
            </AnimatePresence>
          </ul>
        </Loader>
      </div>
    );
  }
}

export default  withGlobalContext(withCommonUtils(Permissions));
